import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    CardRadio,
    CardToggleBasic,
    Checkbox,
    Disclosure,
    DisclosurePanel,
    RadioGroup,
    ScrollFader,
    useScrollFaderController,
} from '@gitbook/spine-react';

import {
    deriveAccessLevel,
    getReadOnlyScopes,
    getScopeDescription,
    getScopesForAccessLevel,
    groupScopesByCategory,
    isSensitiveScope,
    type OAuthConsentFormValues,
} from './scopes';

/**
 * Scope-granting part of the consent screen allowing the user to:
 * - either select an access level (Read-only vs Full access) grouping a set of scopes.
 * - or cherry pick individual scopes from the ones requested by the client.
 */
export function OAuthScopeSelector(props: {
    requestedScopes: string[];
    disabled: boolean;
}): React.ReactElement {
    const { requestedScopes, disabled } = props;
    const { setValue } = useFormContext<OAuthConsentFormValues>();
    const selectedScopes = useWatch<OAuthConsentFormValues, 'scopes'>({ name: 'scopes' }) ?? [];
    const [view, setView] = React.useState<'levels' | 'custom'>('levels');

    // Keep granted scopes in the order the client requested them, so the resulting grant is stable.
    const setScopes = (scopes: string[]) => {
        const next = new Set(scopes);
        setValue(
            'scopes',
            requestedScopes.filter((scope) => next.has(scope)),
            { shouldDirty: true }
        );
    };

    if (view === 'custom') {
        return (
            <OAuthCustomScopes
                requestedScopes={requestedScopes}
                selectedScopes={selectedScopes}
                disabled={disabled}
                onChange={setScopes}
                onReset={() => setScopes(getReadOnlyScopes(requestedScopes))}
                onBack={() => setView('levels')}
            />
        );
    }

    return (
        <OAuthAccessLevels
            requestedScopes={requestedScopes}
            selectedScopes={selectedScopes}
            disabled={disabled}
            onChange={setScopes}
            onCustomize={() => setView('custom')}
        />
    );
}

/**
 * Preset access levels (Read only / Full access).
 */
function OAuthAccessLevels(props: {
    requestedScopes: string[];
    selectedScopes: string[];
    disabled: boolean;
    onChange: (scopes: string[]) => void;
    onCustomize: () => void;
}): React.ReactElement {
    const { requestedScopes, selectedScopes, disabled, onChange, onCustomize } = props;
    const accessLevel = deriveAccessLevel(selectedScopes, requestedScopes);

    return (
        <div className="flex flex-col gap-3">
            <span className="font-medium text-muted text-ui-small uppercase tracking-wide">
                Access level
            </span>
            <RadioGroup
                aria-label="Access level"
                orientation="vertical"
                // An empty value keeps the group controlled while showing no preset selected when the
                // user has cherry-picked a custom set that matches neither preset.
                value={accessLevel === 'custom' ? '' : accessLevel}
                onChange={(level) =>
                    onChange(getScopesForAccessLevel(level as 'read' | 'full', requestedScopes))
                }
                isDisabled={disabled}
            >
                <CardRadio value="read">
                    <CardToggleBasic
                        leadingIcon={Icon.Eye}
                        title="Read only"
                        description="View resources without making changes."
                    />
                </CardRadio>
                <CardRadio value="full">
                    <CardToggleBasic
                        leadingIcon={Icon.Pencil}
                        title="Full access"
                        description="Create, view, modify and delete resources."
                    />
                </CardRadio>
            </RadioGroup>
            <Button
                kind="blank"
                size="small"
                className="-mx-2 self-start"
                trailingIcon={Icon.ChevronRight}
                label="Customize scopes (advanced)"
                onPress={onCustomize}
                disabled={disabled}
            />
        </div>
    );
}

/**
 * Advanced view allowing the user to cherry pick inidividual scopes from the requested ones.
 */
function OAuthCustomScopes(props: {
    requestedScopes: string[];
    selectedScopes: string[];
    disabled: boolean;
    onChange: (scopes: string[]) => void;
    onReset: () => void;
    onBack: () => void;
}): React.ReactElement {
    const { requestedScopes, selectedScopes, disabled, onChange, onReset, onBack } = props;
    const groups = React.useMemo(() => groupScopesByCategory(requestedScopes), [requestedScopes]);
    const selected = React.useMemo(() => new Set(selectedScopes), [selectedScopes]);
    const scrollFader = useScrollFaderController('vertical');

    const toggleScope = (scope: string, checked: boolean) => {
        const next = new Set(selected);
        if (checked) {
            next.add(scope);
        } else {
            next.delete(scope);
        }
        onChange([...next]);
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <Button
                    kind="blank"
                    size="small"
                    className="-mx-2 font-medium uppercase tracking-wide"
                    leadingIcon={Icon.ChevronLeft}
                    label="Access level"
                    onPress={onBack}
                    disabled={disabled}
                />
                <Button
                    kind="blank"
                    size="small"
                    className="-mx-2"
                    leadingIcon={Icon.Reset}
                    label="Reset"
                    onPress={onReset}
                    disabled={disabled}
                />
            </div>
            <ScrollFader controller={scrollFader}>
                <div
                    ref={scrollFader.ref}
                    className="no-scrollbar flex max-h-80 flex-col gap-1 overflow-y-auto"
                >
                    {groups.map((group) => (
                        <OAuthScopeGroup
                            key={group.category}
                            group={group}
                            selected={selected}
                            disabled={disabled}
                            onToggle={toggleScope}
                        />
                    ))}
                </div>
            </ScrollFader>
        </div>
    );
}

function OAuthScopeGroup(props: {
    group: ReturnType<typeof groupScopesByCategory>[number];
    selected: Set<string>;
    disabled: boolean;
    onToggle: (scope: string, checked: boolean) => void;
}): React.ReactElement {
    const { group, selected, disabled, onToggle } = props;
    const [isExpanded, setIsExpanded] = React.useState(true);

    return (
        <Disclosure isExpanded={isExpanded} onExpandedChange={setIsExpanded}>
            <Button
                kind="blank"
                size="small"
                className="w-full"
                leadingIcon={group.icon}
                label={group.label}
                labelStyle="flex-1 text-left font-medium text-base"
                trailingIcon={isExpanded ? Icon.ChevronDown : Icon.ChevronRight}
                onPress={() => setIsExpanded((current) => !current)}
                aria-expanded={isExpanded}
            />
            <DisclosurePanel>
                <div className="flex flex-col gap-px pb-1">
                    {group.scopes.map((scope) => (
                        <div key={scope} className="flex items-center gap-3 py-1.5 pl-6">
                            <Checkbox
                                value={selected.has(scope)}
                                onChange={(checked) => onToggle(scope, checked)}
                                disabled={disabled}
                                size="small"
                                aria-label={getScopeDescription(scope)}
                            />
                            <span className="text-ui-small">
                                {getScopeDescription(scope)}
                                {isSensitiveScope(scope) ? (
                                    <span className="text-muted"> (sensitive)</span>
                                ) : null}
                            </span>
                        </div>
                    ))}
                </div>
            </DisclosurePanel>
        </Disclosure>
    );
}
